import { wearAccessories } from './accessories';
import type * as THREE from 'three';
import type { PlayerBody } from '../player';
import { WALK_SPEED } from '../world/people';
import { retargetedClips, strideScale } from './anims';
import { Human, type Appearance } from './model';
import { makeSkinnedHuman } from './rig';
import { faceSetter } from './expression';
import { slotKey } from '../slot';

/** Where the creator saves the player's character. */
export const CHARACTER_KEY = slotKey('lifesim.character.v1');

export function savedCharacter(): Appearance | null {
  try { return JSON.parse(localStorage.getItem(CHARACTER_KEY) ?? 'null'); } catch { return null; }
}

/** The saved character, skinned and animated, ready to be the player. */
export async function characterBody(appearance: Appearance, renderer: THREE.WebGLRenderer, o: { lite?: boolean } = {}): Promise<PlayerBody> {
  const human = await Human.create(appearance, renderer, o);
  const sex = appearance.macros.gender >= 0.5 ? 'm' : 'f';
  const { group, rig, meshes } = makeSkinnedHuman(human);
  wearAccessories(group, appearance.accessories ?? []);
  const [clips, stride] = await Promise.all([retargetedClips(rig, sex, ['idle', 'walk', 'run', 'sit']), strideScale(rig, sex)]);
  return {
    root: group,
    idle: clips.get(`${sex}_idle`)!,
    walk: clips.get(`${sex}_walk`)!,
    walkSpeed: WALK_SPEED[`${sex}_walk`] * stride,
    run: clips.get(`${sex}_run`),
    runSpeed: WALK_SPEED[`${sex}_run`] * stride,
    sit: clips.get(`${sex}_sit`),
    textured: () => human.settled(),
    free: () => human.dispose(),
    face: faceSetter(meshes, human.parts(), appearance.macros),
    extra: (name) => retargetedClips(rig, sex, [name]).then((m) => m.get(`${sex}_${name}`)).catch(() => undefined),
  };
}
